import React, { useState, useEffect } from "react";
import AuthHeader from "../../../UI/AuthHeader/AuthHeader";
import { useNavigate } from "react-router-dom";
import { useDispatch } from 'react-redux';
import { Modal } from "../../../UI/Modal/Modal";
import Rules from "../../Rules";
import BubbleFloat from "../../../UI/BubbleFloat/BubbleFloat";
import { checkAuthenticatedUser, startExamSession } from "../../../../utils/client-functions";
import { useErrorSetter } from '../../../../hooks/useUiMessages';
import useExamStartGate from "../../../../hooks/useExamStartGate";
import ExamReadyModal from "../../ExamReadyModal";

// NIS Recruitment CBT (practice approximation) mirrors pattern used in NCS component

const Nis = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [authMenuOpen, setAuthMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progressStatus, setProgressStatus] = useState('Gathering Exam Details...');
  const [progressPercentage, setProgressPercentage] = useState(0);
  const { examReady, handleOngoing, handleDoneEvent, confirmStartExam, closeGate } = useExamStartGate(navigate, dispatch);

  const isAuth = localStorage?.getItem("token");
  const errorSetter = useErrorSetter();

  const toggleAuthMenu = () => setAuthMenuOpen(!authMenuOpen);

    useEffect(() => {
        const verifyAuth = async () => {
            try {
                await checkAuthenticatedUser();
            } catch (error) {
                errorSetter("Please login to access mock exams");
                sessionStorage.setItem("prevPath", window.location.pathname);
                navigate("/popin");
            }
        };
        verifyAuth();
    }, [navigate, errorSetter]);

  const nisExamMeta = {
    subject: "Nigeria Immigration Service (NIS) Recruitment CBT",
    timeAlloted: 2700, // 45 minutes (approximation)
    description: `This mock simulates the NIS Recruitment Computer-Based Test (CBT) to help you practice under realistic conditions.\n\nConduct Notes (practice approximation):\n- Remote CBT conditions with identity verification.\n- Focus and integrity: avoid switching tabs/windows.\n- Balanced aptitude domains (English, Quantitative, Logic, General Knowledge/Civic & Immigration Awareness).\n\nBlueprint (approximation – not an official publication):\n- 50 MCQs total; single-answer questions.\n- Mixed difficulty distribution (Easy/Medium/Hard).\n\nDisclaimer: Publicly available detailed NIS blueprint/duration is limited; this structure is an informed practice model for improving speed, accuracy, and familiarity.`,
  };

  const closeModalsAndReset = () => {
    navigate('/user/dashboard/mock');
  };

  const handleStartExam = async () => {
    try {
      await checkAuthenticatedUser();
    } catch (error) {
      errorSetter("Please login to generate an exam");
            sessionStorage.setItem("prevPath", window.location.pathname);
            return navigate("/popin");
    }

    setLoading(true);
    setProgressStatus("Gathering Exam Details...");
    setProgressPercentage(5);

    const payload = {
      subject: nisExamMeta.subject,
      timeAlloted: nisExamMeta.timeAlloted,
      description: nisExamMeta.description,
      examName: "NIS",
    };

    try {
      await startExamSession({
        payload,
        tokenOverride: isAuth,
        onOngoing: (examDetails) => { setLoading(false); handleOngoing(examDetails); },
        onDone: (e) => { handleDoneEvent(e); },
        onError: (msg) => { setLoading(false); errorSetter(msg); },
        setLoading, setProgressStatus, setProgressPercentage
      });
    } catch (error) {
      setLoading(false);
      errorSetter(error?.response?.data?.message || error?.response?.data?.error || "An Error Occurred. Please try again. If issue persists, please chat with us.");
      if (error?.response?.data?.openPricing) {
        window.dispatchEvent(new CustomEvent('openExamPricing'));
      }
    }
  };

  return (
    <div className="auth-container">
      <BubbleFloat />
      <div className="auth-container-inner content">
        <AuthHeader
          authMenuOpen={authMenuOpen}
          onClick={toggleAuthMenu}
          headerText="NIS MOCK"
        />
      </div>

      {!examReady && (
        <Rules
          examName={"Nigeria Immigration Service Recruitment CBT"}
          closeModalsAndReset={closeModalsAndReset}
          subject={nisExamMeta}
          handleStartExam={handleStartExam}
        />
      )}

      {loading && (
        <Modal
          header4={`PROCESSING... DO NOT RELOAD`}
          header3={progressStatus}
          progress={progressPercentage}
        />
      )}
      {examReady && (
        <ExamReadyModal
          isOpen={examReady}
          onClose={closeGate}
          onStart={confirmStartExam}
          examLabel="NIS"
        />
      )}
    </div>
  );
};

export default Nis;

